import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProfileImg from "../../src/assets/images/logo.png";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { EmailRounded } from "@mui/icons-material";

const apiUrl = process.env.REACT_APP_API_URL;
console.log(apiUrl);

// Function to clear access token and redirect to dashboard
const handleLogoutRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const confirmation = urlParams.get("confirmation");
  console.log(confirmation);
  if (confirmation === "logout_success") {
    localStorage.removeItem("accessToken");
    window.location.href = "/";
  }
};

// Call the function on page load
window.onload = handleLogoutRedirect;

const Sidebar = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(0);
  const [activeSubmenuItem, setActiveSubmenuItem] = useState(null);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [isAuditAccess, setIsAuditAccess] = useState("");

  const isMobile = () => window.innerWidth <= 768;
  const [isExpanded, setIsExpanded] = useState(!isMobile());

  // Update active states based on current URL
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.name) {
      setUserName(user.name);
      setEmail(user.email);
      setRole(user.role);
      setIsAuditAccess(user.isAuditAccess);
    }
  }, []);

  const handleLogout = () => {
    window.location.href = `${apiUrl}logout?userName=${encodeURIComponent(
      userName
    )}&email=${encodeURIComponent(email)}`;
  };

  useEffect(() => {
    const currentPath = location.pathname;
    let found = false;

    // Find which menu item matches the current path
    menuItems.forEach((item, index) => {
      if (item.submenu) {
        item.submenu.forEach((subItem, subIndex) => {
          if (currentPath.startsWith(subItem.path)) {
            setActive(index);
            setActiveSubmenuItem(`${index}-${subIndex}`);
            setOpenSubmenus((prev) => ({ ...prev, [index]: true }));
            found = true;
          }
        });
      } else if (currentPath === item.path) {
        setActive(index);
        setActiveSubmenuItem(null);
        setOpenSubmenus({}); // Close all submenus
        found = true;
      }
    });

    // If no match found (e.g., on initial load), reset states
    if (!found) {
      setActiveSubmenuItem(null);
      setOpenSubmenus({});
    }
  }, [location.pathname]);

  let menuItems;

  if (role === "admin") {
    menuItems = [
      {
        name: "Dashboard",
        icon: "bi-house-door",
        path: "/dashboard",
      },
      {
        name: "Manage Emails",
        icon: "bi-envelope",
        path: "/manage-emails",
      },
      {
        name: "Create New Email",
        icon: "bi-envelope",
        path: "/create-email",
      },
      {
        name: "Audit",
        icon: "bi-file-earmark-check",
        submenu: [
          { name: "User Audit", path: "/view-user-audit" },
          { name: "Email Audit", path: "/view-email-audit" },
          { name: "Access", path: "/view-audit-access" },
        ],
      },
    ];
  } else if (role === "user" && isAuditAccess === true) {
    menuItems = [
      {
        name: "Dashboard",
        icon: "bi-house-door",
        path: "/dashboard",
      },
      {
        name: "Manage Emails",
        icon: "bi-envelope",
        path: "/manage-emails",
      },
      {
        name: "Create New Email",
        icon: "bi-envelope",
        path: "/create-email",
      },
      {
        name: "Audit",
        icon: "bi-file-earmark-check",
        submenu: [{ name: "Email Audit", path: "/view-email-audit" }],
      },
    ];
  } else {
    menuItems = [
      {
        name: "Dashboard",
        icon: "bi-house-door",
        path: "/dashboard",
      },
      {
        name: "Manage Emails",
        icon: "bi-envelope",
        path: "/manage-emails",
      },
      {
        name: "Create New Email",
        icon: "bi-envelope",
        path: "/create-email",
      },
    ];
  }

  const logoutItem = [
    {
      name: "Log Out",
      icon: "bi-box-arrow-right",
      path: "/logout",
    },
  ];

  const handleItemClick = (index, item) => {
    setActive(index);
    if (item.submenu) {
      // Toggle submenu
      setOpenSubmenus((prev) => {
        const newState = { ...prev };
        // Close all other submenus
        Object.keys(newState).forEach((key) => {
          if (key !== index.toString()) {
            newState[key] = false;
          }
        });
        // Toggle clicked submenu
        newState[index] = !prev[index];
        return newState;
      });
      setActive(index);
    } else if (item.path === "/logout") {
      handleLogout();
    } else if (item.path) {
      setActive(index);
      setActiveSubmenuItem(null);
      setOpenSubmenus({}); // Close all submenus
      navigate(item.path);
    }
    if (isMobile()) {
      setIsExpanded(false);
    }
  };

  const handleSubmenuClick = (mainIndex, subItem, subIndex) => {
    setActive(mainIndex);
    setActiveSubmenuItem(`${mainIndex}-${subIndex}`);
    // Keep only the parent submenu open
    setOpenSubmenus({ [mainIndex]: true });
    navigate(subItem.path);

    if (isMobile()) {
      setIsExpanded(false);
    }
  };

  const renderMenuItem = (item, index) => {
    const isActive = active === index;
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isSubmenuOpen = openSubmenus[index];

    return (
      <li key={index} className={`menu-item ${isActive ? "active" : ""}`}>
        <div
          className="menu-item-content"
          onClick={() => handleItemClick(index, item)}
        >
          <i className={`bi ${item.icon} menu-item-icon`}></i>
          {isExpanded && <span className="menu-item-name">{item.name}</span>}
          {hasSubmenu && isExpanded && (
            <i
              className={`bi bi-chevron-down submenu-indicator ${
                isSubmenuOpen ? "open" : ""
              }`}
            ></i>
          )}
        </div>
        {hasSubmenu && isSubmenuOpen && isExpanded && (
          <ul className="submenu">
            {item.submenu.map((subItem, subIndex) => (
              <li
                key={subIndex}
                className={`submenu-item ${
                  activeSubmenuItem === `${index}-${subIndex}` ? "active" : ""
                }`}
                onClick={() => handleSubmenuClick(index, subItem, subIndex)}
              >
                {subItem.name}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const renderLogoutItem = (item, index) => {
    return (
      <li key={index} className="menu-item">
        <div
          className="menu-item-content"
          onClick={() => handleItemClick(index, item)}
        >
          <i className={`bi ${item.icon} menu-item-icon`}></i>
          {isExpanded && <span className="menu-item-name">{item.name}</span>}
        </div>
      </li>
    );
  };

  return (
    <nav className={`sidebar ${isExpanded ? "expanded" : ""}`}>
      <div className="sidebar-header">
        <button
          className="toggle-btn"
          onClick={() => {
            setIsExpanded(!isExpanded);
            toggleSidebar();
          }}
        >
          <i className={`bi fs-4 ${isExpanded ? "bi-list" : "bi-list"}`}></i>
        </button>
      </div>
      {isExpanded && (
        <div className="profile-area">
          <img src={ProfileImg} alt="Profile" className="profile-image" />
          <h2 className="profile-name">{userName}</h2>
        </div>
      )}
      <ul className="menu-list">
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </ul>
      <ul className="logout-list">
        {logoutItem.map((item, index) => renderLogoutItem(item, index))}
      </ul>
    </nav>
  );
};

export default Sidebar;
